"use client"

/*
 * DEV HANDOFF - settings-form.tsx
 *
 * The spec-driven renderer behind the settings screens. One row anatomy, one
 * field union, no per-network branching: a new setting is an array entry, not
 * new code.
 *
 * It is deliberately the SAME SHAPE as production's
 * `ui/src/shadcn/components/NewAutomation/types.ts` -> `ConfigureField`, which
 * `ConfigureStep.tsx` already renders and `ui/src/lib/social-input-configure.ts`
 * already builds specs for. `SettingsField` is that type plus the kinds the
 * legacy settings panels need and the wizard did not (`textarea`, `checkbox`
 * group, `tags`, `rules`, `echo`, `static`) plus `width` and `span`, which set
 * how wide a control is and whether a field may break the reading measure.
 * De-duplicate the two into one module at merge time.
 *
 * HELP COPY follows connect-social.tsx exactly: `hint` is the long-form
 * explanation behind an `Info` glyph in the label row, `help` is the short
 * muted line under the control. Same glyph, same trigger classes, same
 * `tw-root` portal patch, same `TooltipProvider`. Two files, one affordance.
 */

import * as React from "react"
import { Check, Info, MapPin, Plus, Sparkles, TriangleAlert, X } from "lucide-react"
import { AdvancedMarker, Map } from "@vis.gl/react-google-maps"

import { HELP_TEXT, META_TEXT, TAP_TARGET } from "@/lib/ui-tokens"
import { SM_BUTTON } from "@/shadcn/components/styles"
import { cn } from "@/lib/utils"
import { Badge } from "@/shadcn/components/ui/badge"
import { Button } from "@/shadcn/components/ui/button"
import { Checkbox } from "@/shadcn/components/ui/checkbox"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/shadcn/components/ui/empty"
import { Input } from "@/shadcn/components/ui/input"
import {
  Item,
  ItemActions,
  ItemContent,
  ItemGroup,
} from "@/shadcn/components/ui/item"
import { Skeleton } from "@/shadcn/components/ui/skeleton"
import { Spinner } from "@/shadcn/components/ui/spinner"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select"
import { Switch } from "@/shadcn/components/ui/switch"
import { Textarea } from "@/shadcn/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcn/components/ui/tooltip"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/shadcn/components/ui/toggle-group"

/* ───────────────────────────────────────────────────────────────── types ── */

export type SettingsOption = string | { value: string; label: string }

/** One find-and-replace rule on the Item text section. */
export type TextRule = {
  id: string
  /** Legacy calls these "replace" and "remove"; remove hides the second field. */
  action: "replace" | "remove"
  search: string
  replace?: string
  caseSensitive?: boolean
  wholeWord?: boolean
  searchTitle?: boolean
  searchBody?: boolean
  /**
   * What the server said about THIS rule.
   *
   * Per row rather than on the field, because `Input::validate` reports per
   * row — `$errs['rules'][$idx]['search']` — and a stack of rules with one
   * message above it does not say which one is wrong, which is the whole
   * complaint the message exists to answer.
   */
  error?: string
}

/** One echo (recycle) schedule: post again after N days and M hours. */
export type EchoRow = {
  id: string
  days: string
  hours: string
  prefix: string
}

/**
 * ONE UPDATE WINDOW. Mirrors a legacy `Schedules` row, so the field names are
 * ours but the shapes are legacy's and the mapping is mechanical:
 *
 *   id    -> s_id       ("sch-<timestamp>" while unsaved)
 *   days  -> s_days     a BITMASK, not a list: Mon 0x01 ... Sun 0x40
 *   start -> s_startTime "HH:MM:SS", always on a 15-minute boundary
 *   end   -> s_endTime   same, EXCEPT "00:00:00" which is not midnight
 *
 * `end: "00:00:00"` MEANS "CHECK ONCE AT `start`", not a window that ends at
 * midnight. Legacy writes it whenever the two slider handles meet
 * (`ScheduleComponent.scheduleUpdate`: "no longer a window, but a single
 * event") and reads it back by drawing both handles at `s_startTime`. It is
 * the one piece of this control that is impossible to guess from the UI, so it
 * is named here and stated on screen.
 *
 * THESE ARE PULL TIMES, NOT POST TIMES. A window decides when the SOURCE IS
 * CHECKED: `Schedule::findNextPullFromSchedules` feeds
 * `Input::updateNextPullBySchedule`, and `Schedule.php:186` is the branch that
 * reads a non-zero end as a span rather than a point. What happens to an item
 * after it has been pulled belongs to the Updates tab's trickle and limits and
 * to the destination's own settings. Copy here says "check", never "post".
 */
export type ScheduleWindow = {
  id: string
  days: number
  start: string
  end: string
}

/**
 * Legacy's day bits (`code/classes/Schedule.php`), in the order legacy's own
 * strip shows them - Sunday first, which is not the bit order.
 *
 * Abbreviations normalised: legacy renders "Tues" and "Thur".
 */
export const SCHEDULE_DAYS: { label: string; bit: number }[] = [
  { label: "Sun", bit: 0x40 },
  { label: "Mon", bit: 0x01 },
  { label: "Tue", bit: 0x02 },
  { label: "Wed", bit: 0x04 },
  { label: "Thu", bit: 0x08 },
  { label: "Fri", bit: 0x10 },
  { label: "Sat", bit: 0x20 },
]

/** `FS_WEEKDAYS` and `FS_WEEKENDS` exist in `Schedule.php` commented
 *  "convenience defines for UI" and legacy's UI never uses them. */
const DAY_PRESETS: { label: string; mask: number }[] = [
  { label: "Every day", mask: 0x7f },
  { label: "Weekdays", mask: 0x1f },
  { label: "Weekends", mask: 0x60 },
]

/** "09:15:00" -> "9:15 am". Legacy formats with moment; this is the same shape. */
export function formatScheduleTime(time: string): string {
  const [h, m] = time.split(":").map((n) => parseInt(n, 10))
  if (Number.isNaN(h) || Number.isNaN(m)) return time
  const meridiem = h < 12 ? "am" : "pm"
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return `${hour12}:${String(m).padStart(2, "0")} ${meridiem}`
}

/** Which days a bitmask names, in the strip's order. */
function dayNames(days: number): string[] {
  return SCHEDULE_DAYS.filter((d) => days & d.bit).map((d) => d.label)
}

/**
 * Every 15-minute boundary, plus 23:59 as the end-of-day option.
 *
 * 15 minutes is legacy's real granularity: its slider runs 0-96 segments of
 * 15 minutes (`ScheduleComponent.timeToInteger`). Two selects rather than that
 * two-handle slider, because legacy ALREADY ships selects on mobile
 * (`TimePickerHour` / `Minute` / `Meridiem` behind a `Breakpoint`), so this is
 * one implementation of a pattern it has two of - and a range slider needs a
 * primitive the kit does not have, collides its own handle labels at short
 * ranges (legacy patches that with a `topper` class), and is the worst control
 * on a phone. Precision is identical.
 */
const TIME_OPTIONS: { value: string; label: string }[] = (() => {
  const out: { value: string; label: string }[] = []
  for (let seg = 0; seg < 96; seg++) {
    const mins = seg * 15
    const v = `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(
      mins % 60
    ).padStart(2, "0")}:00`
    out.push({ value: v, label: formatScheduleTime(v) })
  }
  out.push({ value: "23:59:59", label: "11:59 pm" })
  return out
})()

export type SettingsField = {
  id: string
  label: string
  /** The legacy `data-field` dot path, e.g. "input.attrs.prefixText". */
  name?: string
  /**
   * Which control renders. Defaults to "input".
   *
   * SEGMENTED VS SELECT - use "segmented" (a ToggleGroup, the control the new
   * automation flow uses) whenever the option set is short, closed and worth
   * seeing all at once: up to five options whose labels fit a row at half the
   * content width. Fall back to "select" for longer sets, longer labels, or a
   * list whose length is decided by the backend. Stating the rule here rather
   * than per field is what keeps two screens from drifting apart on it.
   */
  kind?:
    | "toggle"
    | "segmented"
    | "select"
    | "input"
    | "textarea"
    | "checkbox"
    | "tags"
    | "rules"
    | "echo"
    | "action"
    | "map"
    | "reference"
    | "schedule"
    | "static"
  options?: SettingsOption[]
  value?: string
  /** kind: "checkbox" | "tags" */
  values?: string[]
  /** kind: "toggle" | "echo" */
  checked?: boolean
  placeholder?: string
  /** Not drawn: no asterisk on these screens. An empty one errors on save. */
  required?: boolean
  disabled?: boolean
  /** Muted 12px line under the control. */
  help?: string
  /** Trailing link inside `help`, e.g. "Need hashtags?". */
  helpLink?: { label: string; href?: string }
  /**
   * Long-form explanation behind the ⓘ glyph. An ARRAY renders as stacked
   * lines, which is what copy with "Format:" / "Example:" lines needs. Omit and
   * no glyph renders at all.
   */
  hint?: string | string[]
  /**
   * Pins the tooltip open. REVIEW AFFORDANCE ONLY - a hover-only overlay cannot
   * be seen in a screenshot or on touch, so a preview state can force one open.
   * Never set it in production.
   */
  hintOpen?: boolean
  error?: string
  /**
   * A third tone between `error` and `help`, for a state that is not wrong but
   * is not finished either (G4).
   *
   * WHY IT EXISTS: legacy's `FilterPanel.jsx:18` computes `shouldShowWarning()`
   * and shows a triangle warning only while the configuration is HALF DONE -
   * terms entered with no field active, or a field active with no terms. Our
   * screen had that as a permanent help line instead, which says the same thing
   * to everyone forever, including the people who have it right.
   *
   * Uses the theme's `--warning`, already mapped to `--color-warning` in
   * `globals.css:53` and already used by `custom-toast.tsx`, so this adds a
   * tone rather than a colour.
   */
  warning?: string
  /**
   * How wide the CONTROL is inside the form column. Legacy encodes the same
   * thing as `text-input x-small / small / medium`, and a 600px-wide box for a
   * two-digit number reads as a mistake.
   */
  width?: "xs" | "sm" | "full"
  /**
   * "full" breaks the field out of the form's reading measure to the whole
   * content width. For widgets that genuinely need it: rule stacks, tag pools,
   * the location map. Everything else stays in the column.
   */
  span?: "measure" | "full"
  /** Gated behind the user's plan. Renders the upgrade affordance, disables the control. */
  locked?: boolean
  /**
   * Only meaningful in a per-automation override, where a field either carries
   * the destination's global value or one of its own. Legacy's
   * `getParamStateLabel` badges BOTH states ("Default" / "Override"); only
   * "override" is badged here, because the locked product rule is that the
   * default goes unmarked and the exception carries the badge (the same call
   * that leaves instant posting unmarked and badges queued).
   */
  paramState?: "default" | "override"
  /**
   * What the badge explains on hover and focus - the value this field is
   * overriding, as a finished sentence. The DATA supplies the whole string so
   * this renderer stays generic: it has no idea it is inside an override
   * surface, and "destination" is the override screen's word, not this file's.
   *
   * Only read when `paramState` is "override". A badge with no hint is still
   * a badge - the pill scans, the hint explains.
   */
  paramStateHint?: string
  /**
   * Review only, exactly like `hintOpen`: pins the badge's tooltip open so
   * the sentence can be read in a preview. Hover-only copy that no state can
   * show is copy nobody reviews. Never set in prod.
   */
  paramStateHintOpen?: boolean
  /**
   * kind: "tags" - every option the network offers. Present means the values
   * are PICKED from a fetched list (YouTube playlists) rather than typed
   * (hashtags), and the control changes accordingly. See `TagsControl`.
   */
  /** kind: "tags" + options - shown once every option has been picked. */
  allPickedLabel?: string
  /**
   * kind: "tags" + options - the list could not be fetched at all. Replaces
   * the chips, because an empty list and a failed fetch are different problems
   * with different fixes.
   */
  optionsError?: string
  /**
   * kind: "reference" - a name/meaning table the reader consults while filling
   * the field above it, rather than prose explaining a control. Legacy renders
   * exactly this inline on the route Tracking panel
   * (`RouteTrackingPanel.jsx:101`), which is the precedent: a variable list is
   * a lookup, and a tooltip is the wrong container for a lookup.
   */
  refRows?: { name: string; desc: string }[]
  /** kind: "tags" - what an empty pool says. Defaults to "Nothing added yet." */
  emptyLabel?: string
  /**
   * kind: "tags" - a second button beside Add, e.g. the hashtag pool's
   * "Find related", which runs a search and fills `suggestions`.
   */
  secondaryAction?: string
  /**
   * kind: "tags" - tags the search came back with, offered under the field.
   *
   * INLINE, NOT A DIALOG, and that follows legacy rather than departing from
   * it: `InputHashtagPanel`'s `hashtag-modal` is a div rendered inside the
   * fieldset, not an overlay. One already picked is shown as such and removes
   * on click, which is the toggle `addRelatedTag` does.
   */
  suggestions?: string[]
  /** kind: "tags" - the search is still running. */
  suggestionsLoading?: boolean
  /**
   * kind: "tags" - a setting ABOUT the list, rendered inside its box.
   *
   * WHY NESTED RATHER THAN A ROW OF ITS OWN: the divided list puts a hairline
   * between every field, which is right for settings that stand alone and
   * wrong for one that only means anything in terms of the list above it. "How
   * many from the pool per post" read as independent of the pool it counts.
   *
   * It is a full field, so it keeps its own name, kind and value and the host
   * stores it exactly as it would at the top level. Only where it draws
   * changes — which is why `sectionPaths` and `attachErrors` walk into it.
   */
  listField?: SettingsField
  /**
   * kind: "map" - the saved point, or null for "no default location set".
   *
   * NULL IS NOT THE MAP'S CENTRE, and `InputLocationPanel` conflates them: it
   * seeds its marker from `getDefaultLocation()`, which falls back to
   * Portland, so a feed with an empty `defaultGeo` draws a pin and the screen
   * claims a default location the record does not have. Here an unset field
   * draws no pin and says so.
   */
  coords?: { lat: number; lng: number } | null
  /**
   * kind: "map" - where the map opens when nothing is set. Legacy's "dateline
   * portlandia" default, kept, because its geolocation fallback has never
   * worked: `getDefaultLocation()` starts an async `getCurrentPosition` and
   * returns before the callback fires, so every unset feed centres here
   * anyway. Making it work means a location-permission prompt on a settings
   * tab, which is a worse trade than a neutral starting view.
   */
  mapCenter?: { lat: number; lng: number }
  /**
   * A checkbox UNDER the control, carrying its own label.
   *
   * FOR PAIRS THAT ARE ONE DECISION. "Begin posts with" and "Disable space
   * after prefix" are the affix and the one thing you can say about it; split
   * into sibling rows they read as two unrelated settings, and the second
   * loses the only context that explains it.
   *
   * A CHECKBOX UNDER THE FIELD, NOT A SWITCH BESIDE IT (Emily's call). Two
   * reasons, both visible on the screen it replaced. A switch is the row-level
   * affordance everywhere else in this form — one per row, hard right — so a
   * second switch floating mid-row beside an input read as a peer setting
   * rather than a qualifier of the box next to it. And the switches landed
   * wherever the input's width left them, so no two rows agreed on a left
   * edge. A checkbox under the control is subordinate by position and starts
   * on the control's own left edge, so a column of them lines up.
   */
  pairedCheck?: {
    id: string
    /** The legacy dot path, as on a field of its own. */
    name?: string
    label: string
    checked?: boolean
    disabled?: boolean
  }
  /**
   * The id of the control above that decides whether this field exists.
   *
   * PURELY PRESENTATIONAL. What exists is still the data's call — the section
   * builders already leave a governed field out when the governing control says
   * so, and nothing here changes that. This only makes the rows DRAW as
   * belonging to the control: indented, with the hairline above them dropped,
   * so a reader can see which picker they answer to.
   *
   * ONLY SET IT WHERE THE GOVERNING CONTROL IS ON SCREEN. Discord and Slack
   * show the summary pair with no post-style picker above it, and indenting
   * those rows would point at a control that is not there. Same reason the
   * source-type, plan and credential gates do not carry it.
   */
  dependsOn?: string
  /** kind: "rules" */
  rules?: TextRule[]
  /** kind: "schedule" - the update windows, in order. */
  windows?: ScheduleWindow[]
  /** kind: "echo" */
  echoRows?: EchoRow[]
  /** kind: "static" - read-only value, or a placeholder for a widget not yet built. */
  staticValue?: string
  /** kind: "action" - the button's label, e.g. "Reconnect". */
  actionLabel?: string
  /**
   * kind: "action" - the action is the fix for a broken state, so the button
   * leads rather than sits quietly. Legacy swaps the same button between
   * `btn-secondary active` and `btn-primary fixed reconnect-reverse` on
   * `sysActionFlag`.
   */
  urgent?: boolean
}

export type SettingsSection = {
  /** Doubles as the tab value and the scroll anchor id. */
  id: string
  label: string
  /** Muted line under the section title in the card layouts. */
  description?: string
  fields: SettingsField[]
}

/* ──────────────────────────────────────────────────────────────── changes ── */

/**
 * WHAT A CONTROL REPORTS. Not what to store — the host decides that.
 *
 * Every variant carries the whole `field`, so the host has its `name` (the
 * legacy `data-field` dot path), its `id`, its `kind` and its current value
 * without a second lookup. A scalar change is already `requestChange`-shaped
 * once the host reads `field.name` and `value` off it; the repeating widgets
 * cannot be, because "the user removed the second rule" is not a value, and
 * turning it into one is exactly the arithmetic `InputFieldUpdateMixin` does.
 *
 * SEMANTIC RATHER THAN POSITIONAL, deliberately. The alternative was for each
 * control to compute the new array or bitmask itself and emit a finished
 * value. That would put legacy's storage rules — the filter group's five
 * separate booleans, the day bitmask, echo's minute offsets — inside a
 * renderer that is meant to be droppable into a kit with no backend at all.
 */
export type SettingsChange =
  /** A scalar control: input, textarea, select, segmented. */
  | { type: "set"; field: SettingsField; value: string }
  /** A switch: toggle, echo. */
  | { type: "toggle"; field: SettingsField; checked: boolean }
  /** One option of a checkbox group, ticked or cleared. */
  | { type: "option"; field: SettingsField; value: string; checked: boolean }
  /** A tag added or removed. `value` is the chip's text, which is what the
   *  picked list holds — see `TagsControl` on why it is the label. */
  | { type: "tag-add"; field: SettingsField; value: string }
  | { type: "tag-remove"; field: SettingsField; value: string }
  /**
   * The tag field's second button, e.g. the hashtag pool's "Find related".
   * Carries what is in the add box, which is the term legacy searches on.
   */
  | { type: "tag-search"; field: SettingsField; value: string }
  /** Dismiss the suggestions the search returned. */
  | { type: "tag-search-clear"; field: SettingsField }
  /** A repeating row: a rule, an update window, an echo. */
  | { type: "row-add"; field: SettingsField }
  | { type: "row-remove"; field: SettingsField; rowId: string }
  /**
   * `patch` holds only the keys that changed, in the row type's own
   * vocabulary. The host maps those back to legacy's field names, which is the
   * inverse of the mapping that built them.
   *
   * A UNION RATHER THAN AN INTERSECTION: the three row types disagree about
   * `days` — a bitmask on a window, a string of days on an echo — so an
   * intersection types it `never` and nothing can be assigned. The host
   * narrows on `field.kind`, which is the same narrowing `row-add` and
   * `row-remove` need, so nothing is lost by not encoding it here.
   */
  | {
      type: "row-set"
      field: SettingsField
      rowId: string
      patch: Partial<TextRule> | Partial<ScheduleWindow> | Partial<EchoRow>
    }
  /** An action button. The field has no `name`; its `id` is the handle. */
  | { type: "action"; field: SettingsField }
  /**
   * kind: "map". SEMANTIC, like the row changes above and for the same
   * reason: legacy stores `input.defaultGeo` as `[lat, lng]` and an empty
   * array for "none", and that is a storage rule, not something a renderer
   * should be encoding. The host writes the array.
   */
  | { type: "geo-set"; field: SettingsField; lat: number; lng: number }
  | { type: "geo-clear"; field: SettingsField }
  /**
   * The reader pressed the Upgrade badge on a plan-gated field.
   *
   * SEPARATE FROM `action` because the two mean different things to the host:
   * an action runs the field's own job, this one only ever opens the upgrade
   * path. It exists at all because a gated control is DISABLED for every kind
   * but the switch, so without the badge four of them would show a lock with
   * no way past it — worse than legacy, which hangs a handler off each.
   */
  | { type: "upgrade"; field: SettingsField }

/**
 * Undefined means nothing is listening, which is the preview kit's state and
 * has to stay legal — the components are built to drop in before a host
 * exists.
 */
const SettingsChangeContext = React.createContext<
  ((change: SettingsChange) => void) | undefined
>(undefined)

/** The one call every control makes. A no-op when no host is listening. */
function useSettingsChange() {
  const onChange = React.useContext(SettingsChangeContext)

  return React.useCallback(
    (change: SettingsChange) => {
      onChange?.(change)
    },
    [onChange]
  )
}

/* ──────────────────────────────────────────────────────────────── helpers ── */

/**
 * Control widths inside the form column. `full` fills the measure; the two
 * narrow steps exist so a number field is not a 600px box.
 */
const CONTROL_WIDTH = {
  xs: "tw:max-w-24",
  sm: "tw:max-w-60",
  /* Not literally full: a row runs the width of the card, and a select stretched
     across it reads as a mistake. 480px is a comfortable measure for a control
     and its longest option label. `span: "full"` is the opt-out. */
  full: "tw:max-w-[480px]",
} as const

function toPairs(options?: SettingsOption[]) {
  return (options ?? []).map((option) =>
    typeof option === "string" ? { value: option, label: option } : option
  )
}

/** A toggle is a ROW (label left, switch right); everything else stacks. */
function isRow(field: SettingsField) {
  return field.kind === "toggle" || field.kind === "echo"
}

/* ────────────────────────────────────────────────────────────── help copy ── */

/**
 * Label row: text, then the red `*` at 4px, then the help glyph at 6px, then
 * the upgrade badge. Margins rather than flex gap, because the trailing marks
 * sit at different offsets and can appear on the same row.
 *
 * The tooltip here is the SAME ONE as connect-social.tsx's FieldLabel - same
 * `Info` glyph, same trigger classes, same array-stacking `tw-root` content.
 * One help affordance across the app, one set of classes to change.
 */
function FieldLabel({
  field,
  /**
   * Render the title as a plain span instead of a `<label>`.
   *
   * A toggle ROW wraps its whole text block in one `<label htmlFor>` so the
   * row is a hit target, and a `<label>` inside a `<label>` is invalid HTML.
   * The outer one carries the association in that case.
   */
  asText = false,
}: {
  field: SettingsField
  asText?: boolean
}) {
  const Title = asText ? "span" : "label"
  const change = useSettingsChange()

  return (
    <div className="tw:flex tw:min-w-0 tw:items-center">
      <Title
        htmlFor={asText ? undefined : field.id}
        className="tw:text-sm tw:font-medium tw:text-foreground"
      >
        {field.label}
      </Title>
      {/* No required asterisk (Emily, 2026-10-01): a required field that is
          left empty errors on save, and that error says what to do. */}
      {field.hint ? (
        <Tooltip open={field.hintOpen ? true : undefined}>
          <TooltipTrigger asChild>
            {/* aria-label names the BUTTON; the tooltip is its description, so
                the hint isn't announced twice. No `title` - a native tooltip
                would double up with the real one. */}
            <button
              type="button"
              aria-label={`More about ${field.label}`}
              className="tw:ml-1.5 tw:rounded-xs tw:text-muted-foreground tw:outline-none tw:hover:text-foreground tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
            >
              <Info className="tw:size-3.5" />
            </button>
          </TooltipTrigger>
          {/* Portal patch: TooltipContent renders through a Radix Portal, so it
              lands outside `.tw-root` and loses the scoped preflight without
              this. flex-col/items-start because the primitive centres a single
              inline row - multi-line copy has to stack left-aligned. */}
          <TooltipContent
            className={cn(
              "tw-root",
              Array.isArray(field.hint) && "tw:flex-col tw:items-start"
            )}
          >
            {Array.isArray(field.hint)
              ? field.hint.map((line) => <span key={line}>{line}</span>)
              : field.hint}
          </TooltipContent>
        </Tooltip>
      ) : null}
      {field.paramState === "override" ? (
        field.paramStateHint ? (
          /*
           * The badge explains itself on hover and focus. It is a BUTTON, not
           * a bare Badge, for the same reason the hint ⓘ is: a tooltip whose
           * trigger cannot take focus is unreachable by keyboard.
           *
           * KNOWN LIMIT, recorded rather than hidden: a tooltip is
           * hover/focus only, so on touch this sentence has no route at all.
           * If the value being overridden should be readable everywhere, it
           * belongs in the field's `help` line instead, which costs a visible
           * line on overridden rows only. That is a prop away and is the
           * better answer if this turns out to matter on a phone.
           */
          <Tooltip open={field.paramStateHintOpen ? true : undefined}>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`Why ${field.label} is overridden`}
                className="tw:ml-2 tw:shrink-0 tw:rounded-full tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
              >
                <Badge variant="secondary" className="tw:font-normal">
                  Override
                </Badge>
              </button>
            </TooltipTrigger>
            {/* Portal patch, as on the hint tooltip above. */}
            <TooltipContent className="tw-root tw:max-w-[260px]">
              {field.paramStateHint}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Badge variant="secondary" className="tw:ml-2 tw:shrink-0 tw:font-normal">
            Override
          </Badge>
        )
      ) : null}
      {field.locked ? (
        /* A button, not a chip: this is the only way past a gated field whose
           control is disabled, so it has to be reachable by pointer, keyboard
           and screen reader alike. */
        <Badge
          asChild
          variant="secondary"
          className="tw:ml-2 tw:shrink-0 tw:cursor-pointer tw:hover:bg-secondary/80"
        >
          <button type="button" onClick={() => change({ type: "upgrade", field })}>
            Upgrade
          </button>
        </Badge>
      ) : null}
    </div>
  )
}

/**
 * One line only: error, else warning, else help, else nothing.
 *
 * The precedence matters. A field that is both invalid and half-configured has
 * one thing worth saying, and it is the error - a warning underneath it would
 * compete for the same glance.
 */
function FieldFooter({ field }: { field: SettingsField }) {
  /* The link is rendered by whichever line is showing, because it is the same
     link either way: the route out of the problem. An affix carrying a hashtag
     says "Postfix cannot contain #" and then offers Hashtags, which is where
     the reader was trying to go. */
  const link = field.helpLink ? (
    <>
      {" "}
      <a
        href={field.helpLink.href ?? "#"}
        className="tw:rounded-xs tw:underline tw:underline-offset-2 tw:outline-none tw:hover:opacity-80 tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
      >
        {field.helpLink.label}
      </a>
    </>
  ) : null

  if (field.error) {
    return (
      <p id={`${field.id}-error`} className="tw:text-xs tw:text-destructive">
        {field.error}
        {/* Inherits the destructive colour rather than going primary blue: a
            blue link inside a red line reads as a second, unrelated message. */}
        {link}
      </p>
    )
  }

  if (field.warning) {
    return (
      /* Icon + text, as legacy does, because amber alone is not a signal -
         it fails for anyone who cannot separate it from the muted grey. */
      <p className="tw:flex tw:items-start tw:gap-1.5 tw:text-xs tw:text-warning">
        <TriangleAlert className="tw:mt-px tw:size-3.5 tw:shrink-0" />
        <span>{field.warning}</span>
      </p>
    )
  }

  if (!field.help) return null

  return (
    <p className={HELP_TEXT}>
      {field.help}
      {/* Primary blue here, where the surrounding text is muted grey and a
          link has to look like one. */}
      <span className="tw:text-primary">{link}</span>
    </p>
  )
}

/* ─────────────────────────────────────────────────────────────── controls ── */

/** Legacy's default centre, `InputLocationPanel.getDefaultLocation()`. */
const MAP_DEFAULT_CENTER = { lat: 45.5236111, lng: -122.675 }

/**
 * THE DEFAULT-LOCATION PICKER.
 *
 * The INTERACTION is `InputLocationPanel.jsx` unchanged: click the map to drop
 * a pin, click the pin to clear it. Same library, same `mapId`, same options
 * (`defaultZoom: 6`, `disableDefaultUI` with `zoomControl` back on). The
 * `<APIProvider>` and key come from `app-main.js`, as they do for every other
 * map in the app.
 *
 * WHAT CHANGED IS THE SURROUND, because three things about the current screen
 * are not discoverable:
 *
 *   1. An unset feed drew a pin anyway (see `coords`). Now: no pin, and a line
 *      saying what to do.
 *   2. Clicking the pin was the ONLY way to clear it, and nothing said so.
 *      Now: a Clear button, and the help line names both ways.
 *   3. The saved coordinates were never shown, so "is this the point I set?"
 *      could only be answered by squinting at the pin. Now: a readout.
 */
function MapControl({ field }: { field: SettingsField }) {
  const change = useSettingsChange()
  const center = field.mapCenter ?? field.coords ?? MAP_DEFAULT_CENTER
  const coords = field.coords ?? null
  const disabled = field.disabled || field.locked

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
      <div
        /* 280px is a map you can aim at on a phone without it taking the whole
           screen. No scale step matches. */
        className={cn(
          "tw:relative tw:h-[280px] tw:w-full tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-muted",
          disabled && "tw:pointer-events-none tw:opacity-50"
        )}
      >
        {/* The preflight opt-out wraps the map and NOTHING else — see
            `.tw-gmap` in tw.css. The prompt below is a sibling of this div, so
            it keeps its own utilities. */}
        <div className="tw-gmap tw:h-full tw:w-full">
          <Map
            mapId="41e3688e931f3e8d"
            defaultCenter={center}
            defaultZoom={6}
            disableDefaultUI
            zoomControl
            gestureHandling="auto"
            onClick={(event) => {
              const latLng = event.detail?.latLng
              if (!latLng) return
              change({ type: "geo-set", field, lat: latLng.lat, lng: latLng.lng })
            }}
          >
            {coords ? (
              <AdvancedMarker
                position={coords}
                onClick={() => change({ type: "geo-clear", field })}
              />
            ) : null}
          </Map>
        </div>

        {/* `pointer-events-none` so the prompt cannot swallow the click it is
            asking for. */}
        {coords ? null : (
          <span className="tw:pointer-events-none tw:absolute tw:inset-x-0 tw:bottom-0 tw:bg-card/90 tw:px-4 tw:py-2 tw:text-center tw:text-sm tw:text-muted-foreground">
            Click the map to set a default location.
          </span>
        )}
      </div>

      {/* Absent until there is something to report, so an unset field is the
          map and nothing else. */}
      {coords ? (
        <div className="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
          <p className={cn(META_TEXT, "tw:font-mono")}>
            {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={SM_BUTTON}
            disabled={disabled}
            onClick={() => change({ type: "geo-clear", field })}
          >
            Clear location
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function SelectControl({ field }: { field: SettingsField }) {
  const options = toPairs(field.options)
  const change = useSettingsChange()

  return (
    <Select
      value={field.value}
      disabled={field.disabled || field.locked}
      onValueChange={(value) => change({ type: "set", field, value })}
    >
      {/* h-11! / md:h-8! rather than TAP_TARGET: SelectTrigger sizes itself with
          `data-[size=default]:h-8`, a class+attribute selector at specificity
          (0,2,0), so a plain h-11 (0,1,0) loses to it whatever the source
          order. w-full needs no `!` - it and the primitive's w-fit are one
          tailwind-merge group. */}
      <SelectTrigger id={field.id} className="tw:h-11! tw:w-full tw:md:h-8!">
        <SelectValue placeholder={field.placeholder} />
      </SelectTrigger>
      {/* Portal patch: content renders outside `.tw-root`. */}
      <SelectContent className="tw-root">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * The segmented control the new automation flow uses, with the responsive swap
 * `create-automation.tsx`'s `ChoiceField` already established: a dropdown below
 * `sm`, the ToggleGroup from `sm` up.
 *
 * The swap is not decoration. `spacing={0}` joins the items by killing the
 * inner borders and rounding only the first and last, all of which assumes ONE
 * ROW - let it wrap at 375px and the second line renders as a detached box with
 * no left border and the wrong radius. So the narrow screen gets a real
 * dropdown instead of a broken row.
 *
 * DIVERGENCE FROM `ChoiceField`, deliberately: it uses a `DropdownMenu` for the
 * narrow trigger; this uses `Select`. Two species of dropdown inside one
 * settings form, some Select and some DropdownMenu, would be the bigger
 * inconsistency. `ChoiceField` should probably move to `Select` too.
 */
function SegmentedControl({ field }: { field: SettingsField }) {
  const options = toPairs(field.options)
  const disabled = field.disabled || field.locked
  const change = useSettingsChange()
  /* Both halves of the responsive swap report the same thing; only one of them
     is mounted at a time, so there is no double-fire to guard against. */
  const select = (value: string) => change({ type: "set", field, value })

  return (
    <>
      {/* id lives here, not on the ToggleGroup: `htmlFor` needs a labelable
          element and a ToggleGroup is a div. */}
      <Select value={field.value} disabled={disabled} onValueChange={select}>
        <SelectTrigger
          id={field.id}
          className="tw:h-11! tw:w-full tw:sm:hidden"
        >
          <SelectValue placeholder={field.placeholder} />
        </SelectTrigger>
        {/* Portal patch: content renders outside `.tw-root`. */}
        <SelectContent className="tw-root">
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ToggleGroup
        type="single"
        spacing={0}
        variant="outline"
        aria-label={field.label}
        /* "" rather than undefined: undefined makes a Radix ToggleGroup
           uncontrolled, so it starts tracking its own selection and drifts from
           the spec. An empty string matches no item, which is the truth. */
        value={field.value ?? ""}
        disabled={disabled}
        /* A single ToggleGroup reports "" when the active item is clicked
           again. That is a deselect, and none of these fields has an unset
           state to deselect into, so it is dropped rather than stored. */
        onValueChange={(value) => {
          if (value) select(value)
        }}
        className="tw:hidden tw:w-fit tw:max-w-full tw:sm:flex"
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            className={TAP_TARGET}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </>
  )
}

/** A set of independent checkboxes, e.g. the five filter sources. */
function CheckboxGroup({ field }: { field: SettingsField }) {
  const options = toPairs(field.options)
  const selected = field.values ?? []
  const change = useSettingsChange()

  return (
    <div className="tw:flex tw:flex-wrap tw:gap-x-6 tw:gap-y-1 tw:md:gap-y-3">
      {options.map((option) => (
        /*
         * THE WHOLE ROW IS THE LABEL, for the same reason the toggle rows are:
         * a 16px box is a miss-tap generator on a phone, and the text beside it
         * was only 20px tall, so the associated label barely helped. Wrapping
         * gives a 44px target - the height `TAP_TARGET` sets for every other
         * control here - that collapses to the compact 24px on `md`, where a
         * cursor makes the small box fine.
         *
         * A click landing on the Checkbox itself is handled by the Checkbox; a
         * click on the text or the padding around it is forwarded by `htmlFor`.
         * The text is a `span`, not a nested `label`, which would be invalid.
         */
        <label
          key={option.value}
          htmlFor={`${field.id}-${option.value}`}
          className={cn(
            "tw:flex tw:min-h-11 tw:items-center tw:gap-2 tw:md:min-h-6",
            field.disabled || field.locked
              ? "tw:cursor-default"
              : "tw:cursor-pointer tw:select-none"
          )}
        >
          <Checkbox
            id={`${field.id}-${option.value}`}
            checked={selected.includes(option.value)}
            disabled={field.disabled || field.locked}
            onCheckedChange={(checked) =>
              change({
                type: "option",
                field,
                value: option.value,
                checked: checked === true,
              })
            }
          />
          <span className="tw:text-sm tw:text-foreground">{option.label}</span>
        </label>
      ))}
    </div>
  )
}

/** Hashtag pool / fixed hashtags: an add field over a removable chip list. */
/*
 * TWO KINDS OF TAG FIELD, and `options` is what tells them apart.
 *
 * WITHOUT `options` the values are free text the user invents - hashtags. A
 * text input plus Add is right: anything can be typed.
 *
 * WITH `options` the values come from a FIXED LIST fetched from the network,
 * and inventing one is meaningless. YouTube playlists are this: legacy uses a
 * `Multiselect` over `props.socials.playlists`, loaded by
 * `SOCIAL_GET_PLAYLISTS` (`YouTubePlaylistChooser.jsx`). Rendering those as a
 * free-text input told the user they could type a playlist name and have it
 * created, which is not what happens - so the control, not just its
 * placeholder, was wrong.
 *
 * `emptyLabel` covers "you have not picked any yet". The other two states
 * legacy distinguishes are `allPickedLabel` (every option is already chosen,
 * so the picker has nothing left to offer) and `optionsError` (the fetch
 * failed and the list cannot be shown at all - legacy's is "Reconnect this
 * channel to access your playlists", which is a different problem with a
 * different fix and must not read as an empty list).
 */
function TagsControl({ field }: { field: SettingsField }) {
  const tags = field.values ?? []
  const options = toPairs(field.options)
  const change = useSettingsChange()
  /*
   * The only local state in this file. A half-typed tag is not a setting —
   * nothing stores it, and pushing it through the host on every keystroke
   * would mark the record dirty for text the user has not added yet. It
   * belongs to the control until Add is pressed.
   */
  const [draft, setDraft] = React.useState("")

  const add = (value: string) => {
    const trimmed = value.trim()

    if (!trimmed) return

    change({ type: "tag-add", field, value: trimmed })
    setDraft("")
  }

  if (options.length || field.optionsError) {
    const taken = new Set(tags)
    const available = options.filter((option) => !taken.has(option.label))
    const disabled = field.disabled || field.locked || Boolean(field.optionsError)

    return (
      <div className="tw:flex tw:w-full tw:flex-col tw:gap-3">
        <Select
          value=""
          disabled={disabled || available.length === 0}
          /* The chips hold LABELS, so that is what is reported — see this
             control's own note above. The value is the option id, which the
             host does not need and the chip could not render. */
          onValueChange={(value) =>
            add(available.find((option) => option.value === value)?.label ?? "")
          }
        >
          <SelectTrigger
            id={field.id}
            className="tw:h-11! tw:w-full tw:md:h-8! tw:sm:max-w-[480px]"
          >
            <SelectValue placeholder={field.placeholder} />
          </SelectTrigger>
          <SelectContent className="tw-root">
            {available.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.optionsError ? (
          <p className="tw:text-xs tw:text-destructive">{field.optionsError}</p>
        ) : tags.length ? (
          <div className="tw:flex tw:flex-wrap tw:gap-1.5">
            {tags.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="tw:gap-1 tw:pr-1 tw:font-normal"
              >
                {tag}
                <button
                  type="button"
                  aria-label={`Remove ${tag}`}
                  disabled={disabled}
                  onClick={() => change({ type: "tag-remove", field, value: tag })}
                  className="tw:flex tw:size-4 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-xs tw:text-muted-foreground tw:outline-none tw:hover:text-foreground tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:disabled:cursor-default"
                >
                  <X className="tw:size-3" />
                </button>
              </Badge>
            ))}
          </div>
        ) : (
          <p className={META_TEXT}>{field.emptyLabel ?? "Nothing added yet."}</p>
        )}
        {tags.length && available.length === 0 && field.allPickedLabel ? (
          <p className={META_TEXT}>{field.allPickedLabel}</p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-3">
      <div className="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:gap-2">
        <Input
          id={field.id}
          placeholder={field.placeholder}
          disabled={field.disabled || field.locked}
          /*
           * FILLS THE ROW UP TO THE FORM'S OWN MEASURE, rather than sitting at
           * a fixed 256px. At `sm:w-64` the Playlists placeholder clipped
           * mid-word ("...YouTube playlist|"), and a fixed width means any
           * copy change can clip it again.
           *
           * `basis-full` below `sm` (its own line, full width, as before),
           * `sm:basis-0` + grow from there, capped at the 480px `CONTROL_WIDTH.full`
           * uses - so it is as long as the row allows without stretching across
           * a 900px card, which reads as a mistake on a single-line input.
           * Grow/shrink/basis are set separately, not via `flex-1`: that
           * shorthand also writes flex-basis and which one won would come down
           * to Tailwind's emit order.
           */
          className={cn(
            TAP_TARGET,
            "tw:min-w-0 tw:shrink tw:grow tw:basis-full tw:sm:basis-0 tw:sm:max-w-[480px]"
          )}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          /* Legacy adds on Enter as well as on the button
             (`InputHashtagPanel.onKeyUp`), and inside a form Enter would
             otherwise submit. */
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              add(draft)
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          className={TAP_TARGET}
          disabled={field.disabled || field.locked || draft.trim() === ""}
          onClick={() => add(draft)}
        >
          <Plus />
          Add
        </Button>
        {/* Legacy's "Find Related" sits beside Add and opens a tag-search
            modal. The modal is not built; the affordance is, so the row is not
            missing a control that exists today. */}
        {field.secondaryAction ? (
          <Button
            type="button"
            variant="outline"
            className={TAP_TARGET}
            disabled={field.disabled || field.locked || draft.trim() === ""}
            onClick={() => change({ type: "tag-search", field, value: draft.trim() })}
          >
            <Sparkles />
            {field.secondaryAction}
          </Button>
        ) : null}
      </div>
      {tags.length ? (
        <div className="tw:flex tw:flex-wrap tw:gap-1.5">
          {tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              className="tw:gap-1 tw:pr-1 tw:font-normal"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                disabled={field.disabled || field.locked}
                onClick={() => change({ type: "tag-remove", field, value: tag })}
                className="tw:flex tw:size-4 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-xs tw:text-muted-foreground tw:outline-none tw:hover:text-foreground tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:disabled:cursor-default"
              >
                <X className="tw:size-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : (
        <p className={META_TEXT}>{field.emptyLabel ?? "Nothing added yet."}</p>
      )}
      <TagSuggestions field={field} tags={tags} />
      <ListSetting field={field.listField} />
    </div>
  )
}

/**
 * A setting about the list, inside the list's own box.
 *
 * Divided from the chips by a hairline so it is clearly a different kind of
 * thing from the tags above it, and clearly still part of the same control —
 * which a sibling row in the outer list could not say.
 */
function ListSetting({ field }: { field?: SettingsField }) {
  if (!field) return null

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:pt-3">
      <FieldLabel field={field} />
      <Control field={field} />
      <FieldFooter field={field} />
    </div>
  )
}

/**
 * What the related-tag search came back with.
 *
 * Each one is a button that adds, or removes if it is already in the list —
 * the toggle `addRelatedTag` performs. Shown as OUTLINE chips so they read as
 * on offer rather than already chosen, against the solid chips above.
 */
function TagSuggestions({
  field,
  tags,
}: {
  field: SettingsField
  tags: string[]
}) {
  const change = useSettingsChange()

  if (field.suggestionsLoading) {
    return (
      <p className={cn(META_TEXT, "tw:flex tw:items-center tw:gap-2")}>
        <Spinner className="tw:size-3.5" />
        Looking for related hashtags...
      </p>
    )
  }

  if (!field.suggestions?.length) return null

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-3">
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <span className={META_TEXT}>Related hashtags</span>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label="Dismiss suggestions"
          onClick={() => change({ type: "tag-search-clear", field })}
          className="tw:text-muted-foreground tw:hover:text-foreground"
        >
          <X />
        </Button>
      </div>
      <div className="tw:flex tw:flex-wrap tw:gap-1.5">
        {field.suggestions.map((suggestion) => {
          const picked = tags.indexOf(suggestion) !== -1

          return (
            <Badge
              key={suggestion}
              asChild
              variant={picked ? "secondary" : "outline"}
              className="tw:cursor-pointer tw:font-normal"
            >
              <button
                type="button"
                aria-pressed={picked}
                onClick={() =>
                  change({
                    type: picked ? "tag-remove" : "tag-add",
                    field,
                    value: suggestion,
                  })
                }
              >
                {picked ? <Check className="tw:size-3" /> : null}
                {suggestion}
              </button>
            </Badge>
          )
        })}
      </div>
    </div>
  )
}

const RULE_ACTIONS: SettingsOption[] = [
  { value: "replace", label: "Replace" },
  { value: "remove", label: "Remove" },
]

const RULE_FLAGS: { key: keyof TextRule; label: string }[] = [
  { key: "caseSensitive", label: "Case sensitive" },
  { key: "wholeWord", label: "Match whole word" },
  { key: "searchTitle", label: "Search title" },
  { key: "searchBody", label: "Search body" },
]

/** Find and replace: a stack of removable rule rows plus an add button. */
function RulesControl({ field }: { field: SettingsField }) {
  const rules = field.rules ?? []
  const change = useSettingsChange()
  const set = (rowId: string, patch: Partial<TextRule>) =>
    change({ type: "row-set", field, rowId, patch })

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-3">
      {rules.map((rule) => (
        <div
          key={rule.id}
          className={cn(
            "tw:flex tw:w-full tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:bg-card tw:p-3",
            rule.error ? "tw:border-destructive" : "tw:border-border"
          )}
        >
          {/*
            * ONE ROW ON DESKTOP, THREE LINES ON A PHONE, from one flat
            * `flex-wrap` row. Two things make that work, and the first is the
            * bug this replaced:
            *
            * 1. `flex-1` is `flex: 1 1 0%`, so with `min-w-0` the inputs had
            *    no width they were unwilling to give up - they shrank to ~30px
            *    at 375px ("Te", "TC") instead of wrapping, because a row only
            *    wraps when an item cannot fit its BASIS. So the basis is
            *    `basis-full` below `sm` (each input claims its own line) and
            *    `sm:basis-0` from `sm` up (share the row). Set as
            *    grow/shrink/basis separately, NOT via `flex-1`: `flex` is a
            *    shorthand that also writes flex-basis, and which of the two
            *    wins would come down to Tailwind's emit order rather than the
            *    order written here.
            * 2. The remove X sits after the toggle IN THE DOM, not last, so
            *    the wrapped layout reads [action + X] / [search] / [replace]
            *    instead of leaving the X stranded alone on a fourth line.
            *    `ml-auto` pushes it to the right edge of that first line;
            *    `sm:order-last` puts it back at the end of the single row from
            *    `sm` up, and `sm:ml-0` hands the spacing back to `gap`.
            */}
          <div className="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:gap-2">
            {/* Two short options, so the same segmented control the rest of the
                form uses, not a dropdown hiding one word behind another. */}
            <ToggleGroup
              type="single"
              spacing={0}
              variant="outline"
              value={rule.action}
              onValueChange={(value) => {
                if (value === "replace" || value === "remove") {
                  set(rule.id, { action: value })
                }
              }}
              className="tw:w-fit tw:shrink-0"
            >
              {toPairs(RULE_ACTIONS).map((option) => (
                <ToggleGroupItem
                  key={option.value}
                  value={option.value}
                  className={TAP_TARGET}
                >
                  {option.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remove rule"
              onClick={() => change({ type: "row-remove", field, rowId: rule.id })}
              className="tw:ml-auto tw:shrink-0 tw:text-muted-foreground tw:hover:text-foreground tw:sm:order-last tw:sm:ml-0"
            >
              <X />
            </Button>
            <Input
              defaultValue={rule.search}
              placeholder="Search for text"
              onChange={(event) => set(rule.id, { search: event.target.value })}
              className={cn(
                TAP_TARGET,
                "tw:min-w-0 tw:shrink tw:grow tw:basis-full tw:sm:basis-0"
              )}
            />
            {rule.action === "replace" ? (
              <Input
                defaultValue={rule.replace}
                placeholder="Replace with text"
                onChange={(event) =>
                  set(rule.id, { replace: event.target.value })
                }
                className={cn(
                  TAP_TARGET,
                  "tw:min-w-0 tw:shrink tw:grow tw:basis-full tw:sm:basis-0"
                )}
              />
            ) : null}
          </div>
          {/* Under the row's own inputs, so it points at the rule it belongs
              to rather than at the stack. */}
          {rule.error ? (
            <p className="tw:text-xs tw:text-destructive">{rule.error}</p>
          ) : null}
          {/* Same 44px-on-mobile treatment as the filter checkboxes above. */}
          <div className="tw:flex tw:flex-wrap tw:gap-x-5 tw:gap-y-0 tw:md:gap-y-2">
            {RULE_FLAGS.map((flag) => (
              <label
                key={flag.key as string}
                htmlFor={`${rule.id}-${flag.key as string}`}
                className="tw:flex tw:min-h-11 tw:cursor-pointer tw:items-center tw:gap-2 tw:select-none tw:md:min-h-5"
              >
                <Checkbox
                  id={`${rule.id}-${flag.key as string}`}
                  checked={Boolean(rule[flag.key])}
                  onCheckedChange={(checked) =>
                    set(rule.id, { [flag.key]: checked === true })
                  }
                />
                <span className="tw:text-xs tw:text-muted-foreground">
                  {flag.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        className={cn(TAP_TARGET, "tw:w-fit")}
        onClick={() => change({ type: "row-add", field })}
      >
        <Plus />
        Add rule
      </Button>
    </div>
  )
}

/**
 * ONE UPDATE WINDOW, as a card. See `ScheduleWindow` for the legacy mapping.
 *
 * Two things legacy leaves invisible are said out loud here, because both are
 * states where the window silently does nothing or does something other than
 * what it looks like:
 *
 *  - **no days picked.** A window added in legacy starts at `s_days: 0`
 *    (`InputFieldUpdateMixin` `case 'add'`), so the thing you just created is
 *    inert and nothing says so.
 *  - **start == end.** Legacy stores that as `s_endTime: "00:00:00"` and means
 *    "check once at this time". Nothing on screen distinguishes it from a
 *    window.
 */
function ScheduleWindowCard({
  field,
  window: w,
}: {
  field: SettingsField
  window: ScheduleWindow
}) {
  const change = useSettingsChange()
  const set = (patch: Partial<ScheduleWindow>) =>
    change({ type: "row-set", field, rowId: w.id, patch })
  const selected = SCHEDULE_DAYS.filter((d) => w.days & d.bit).map((d) =>
    String(d.bit)
  )
  const names = dayNames(w.days)
  const single = w.end === "00:00:00" || w.end === w.start
  const noDays = w.days === 0

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-3">
      <div className="tw:flex tw:w-full tw:flex-wrap tw:items-start tw:gap-2">
        {/* The plain-language reading of the row, which legacy never gives:
            the days and times are controls, this is what they add up to. */}
        <p className="tw:min-w-0 tw:flex-1 tw:text-sm tw:font-medium tw:text-foreground">
          {noDays ? (
            <span className="tw:text-muted-foreground">No days picked yet</span>
          ) : (
            <>
              {names.join(", ")}
              <span className="tw:text-muted-foreground">
                {single
                  ? ` · checks once at ${formatScheduleTime(w.start)}`
                  : ` · ${formatScheduleTime(w.start)} to ${formatScheduleTime(w.end)}`}
              </span>
            </>
          )}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Remove this window`}
          onClick={() => change({ type: "row-remove", field, rowId: w.id })}
          className="tw:shrink-0 tw:text-muted-foreground tw:hover:text-foreground"
        >
          <X />
        </Button>
      </div>

      <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
        <span className={META_TEXT}>Days</span>
        {/* Wraps rather than scrolls: seven two-letter-ish toggles fit two rows
            on a phone, and a horizontally scrolling day strip hides days. */}
        <ToggleGroup
          type="multiple"
          variant="outline"
          value={selected}
          /* The strip reports the bits that are on; the mask is their sum.
             Reporting `days` rather than the list keeps `ScheduleWindow` the
             one shape both sides speak. */
          onValueChange={(bits: string[]) =>
            set({
              days: bits.reduce((mask, bit) => mask | parseInt(bit, 10), 0),
            })
          }
          className="tw:w-fit tw:flex-wrap"
        >
          {SCHEDULE_DAYS.map((d) => (
            <ToggleGroupItem
              key={d.bit}
              value={String(d.bit)}
              aria-label={d.label}
              className={cn(TAP_TARGET, "tw:px-3")}
            >
              {d.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <div className="tw:flex tw:flex-wrap tw:gap-1.5">
          {DAY_PRESETS.map((p) => (
            <Button
              key={p.label}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => set({ days: p.mask })}
              className={cn(
                SM_BUTTON,
                w.days === p.mask && "tw:border-primary tw:text-primary"
              )}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="tw:flex tw:w-full tw:flex-wrap tw:items-end tw:gap-2">
        <div className="tw:flex tw:min-w-0 tw:shrink tw:grow tw:basis-full tw:flex-col tw:gap-1 tw:sm:basis-0">
          <span className={META_TEXT}>From</span>
          <Select value={w.start} onValueChange={(start) => set({ start })}>
            <SelectTrigger className="tw:h-11! tw:w-full tw:md:h-8!">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="tw-root">
              {TIME_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="tw:flex tw:min-w-0 tw:shrink tw:grow tw:basis-full tw:flex-col tw:gap-1 tw:sm:basis-0">
          <span className={META_TEXT}>To</span>
          {/* Reports the time the reader picked. Legacy's "both handles met, so
              store 00:00:00 and mean check-once-at-start" is a STORAGE rule and
              stays
              with the host, which is also where it is read back. */}
          <Select value={single ? w.start : w.end} onValueChange={(end) => set({ end })}>
            <SelectTrigger className="tw:h-11! tw:w-full tw:md:h-8!">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="tw-root">
              {TIME_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {noDays ? (
        <p className={HELP_TEXT}>
          Pick at least one day, or this window does nothing.
        </p>
      ) : single ? (
        <p className={HELP_TEXT}>
          Both times are the same, so we check this source once at{" "}
          {formatScheduleTime(w.start)} rather than through a window.
        </p>
      ) : null}
    </div>
  )
}

/** The window list: every window, or an empty state, plus Add window. */
function ScheduleControl({ field }: { field: SettingsField }) {
  const windows = field.windows ?? []
  const change = useSettingsChange()

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-3">
      {windows.length ? (
        windows.map((w) => (
          <ScheduleWindowCard key={w.id} field={field} window={w} />
        ))
      ) : (
        /* G3 asks for an empty state rather than a placeholder here, and the
           `empty` primitive was installed and unadopted. With no windows the
           source is checked on its normal frequency all day, so that is what
           the empty state has to say - "none" is not "off". */
        <Empty className="tw:rounded-lg tw:border tw:border-dashed tw:border-border tw:bg-card tw:py-6">
          <EmptyHeader>
            <EmptyTitle className="tw:text-sm">No update windows</EmptyTitle>
            <EmptyDescription>
              We check this source around the clock, on the frequency above. Add
              a window to limit checking to certain days and times.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      <Button
        type="button"
        variant="outline"
        className={cn(TAP_TARGET, "tw:w-fit")}
        onClick={() => change({ type: "row-add", field })}
      >
        <Plus />
        Add window
      </Button>
    </div>
  )
}

const ECHO_DAYS = Array.from({ length: 30 }, (_, i) => String(i + 1))
const ECHO_HOURS = Array.from({ length: 24 }, (_, i) => String(i))

/** Echo: the toggle's revealed body - one row per recycle, up to two. */
function EchoRows({ field }: { field: SettingsField }) {
  const rows = field.echoRows ?? []
  const change = useSettingsChange()
  const set = (rowId: string, patch: Partial<EchoRow>) =>
    change({ type: "row-set", field, rowId, patch })

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:bg-card tw:p-3">
      <p className={HELP_TEXT}>
        After we post an item, queue it for recycling:
      </p>
      {rows.map((row, index) => (
        /*
          * Same wrap rule as the Find-and-replace row, and the same reason:
          * `flex-1 min-w-0` on the Prefix column let it collapse to 22px at
          * 375px rather than wrapping, because the two 80px selects and the
          * "After first post" caption together left it nothing. So Prefix gets
          * `basis-full` below `sm`, and the caption LEADS the row on a phone
          * (`basis-full`, moved back to the end by `sm:order-last`), giving:
          *
          *   After first post
          *   [Days] [Hours]
          *   [Prefix            ]
          *
          * instead of one unreadable line. `sm:pb-2` restores the baseline
          * nudge that only makes sense while the caption is trailing.
          *
          * The caption and the remove X travel TOGETHER in one box, so the X
          * lands beside the caption on a phone rather than stranded on a
          * fourth line below the prefix — the same problem, and the same
          * answer, as the Find-and-replace row's X.
          */
        <div
          key={row.id}
          className="tw:flex tw:w-full tw:flex-wrap tw:items-end tw:gap-2"
        >
          <div className="tw:flex tw:basis-full tw:items-center tw:gap-1 tw:sm:order-last tw:sm:basis-auto tw:sm:pb-2">
            <span className={META_TEXT}>
              {index === 0 ? "After first post" : "After second post"}
            </span>
            {/*
              * ONLY THE SECOND ROW CAN GO, which is legacy's rule
              * (`replayControl(1, true)`): the first echo is what the Echo
              * switch itself turns on and off, so removing it here would be a
              * second control for the same state.
              */}
            {index > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="Remove second echo"
                onClick={() => change({ type: "row-remove", field, rowId: row.id })}
                className="tw:ml-auto tw:shrink-0 tw:text-muted-foreground tw:hover:text-foreground tw:sm:ml-0"
              >
                <X />
              </Button>
            ) : null}
          </div>
          <div className="tw:flex tw:flex-col tw:gap-1">
            <span className={META_TEXT}>Days</span>
            <Select value={row.days} onValueChange={(days) => set(row.id, { days })}>
              <SelectTrigger className="tw:h-11! tw:w-20 tw:md:h-8!">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="tw-root">
                {ECHO_DAYS.map((day) => (
                  <SelectItem key={day} value={day}>
                    {day}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="tw:flex tw:flex-col tw:gap-1">
            <span className={META_TEXT}>Hours</span>
            <Select
              value={row.hours}
              onValueChange={(hours) => set(row.id, { hours })}
            >
              <SelectTrigger className="tw:h-11! tw:w-20 tw:md:h-8!">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="tw-root">
                {ECHO_HOURS.map((hour) => (
                  <SelectItem key={hour} value={hour}>
                    {hour}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="tw:flex tw:min-w-0 tw:shrink tw:grow tw:basis-full tw:flex-col tw:gap-1 tw:sm:basis-0">
            <span className={META_TEXT}>Prefix</span>
            <Input
              defaultValue={row.prefix}
              onChange={(event) => set(row.id, { prefix: event.target.value })}
              className={cn(TAP_TARGET, "tw:w-full")}
            />
          </div>
        </div>
      ))}
      {rows.length < 2 ? (
        <Button
          type="button"
          variant="outline"
          className={cn(TAP_TARGET, "tw:w-fit")}
          onClick={() => change({ type: "row-add", field })}
        >
          <Plus />
          Add second echo
        </Button>
      ) : null}
    </div>
  )
}

/** The control for a stacked (non-row) field. */
function Control({ field }: { field: SettingsField }) {
  const change = useSettingsChange()

  switch (field.kind) {
    case "select":
      return <SelectControl field={field} />
    case "segmented":
      return <SegmentedControl field={field} />
    case "checkbox":
      return <CheckboxGroup field={field} />
    case "tags":
      return <TagsControl field={field} />
    case "rules":
      return <RulesControl field={field} />
    case "textarea":
      return (
        <Textarea
          id={field.id}
          defaultValue={field.value}
          placeholder={field.placeholder}
          disabled={field.disabled || field.locked}
          aria-invalid={field.error ? true : undefined}
          onChange={(event) =>
            change({ type: "set", field, value: event.target.value })
          }
          className="tw:min-h-20"
        />
      )
    case "action":
      return (
        <Button
          type="button"
          variant={field.urgent ? "default" : "outline"}
          disabled={field.disabled}
          onClick={() => change({ type: "action", field })}
          className={cn(TAP_TARGET, "tw:w-fit")}
        >
          {field.actionLabel ?? field.label}
        </Button>
      )
    case "schedule":
      return <ScheduleControl field={field} />

    case "reference":
      return (
        <dl className="tw:m-0 tw:grid tw:w-full tw:grid-cols-1 tw:gap-x-4 tw:gap-y-2 tw:sm:grid-cols-[auto_1fr]">
          {(field.refRows ?? []).map((row) => (
            <React.Fragment key={row.name}>
              <dt className="tw:font-mono tw:text-xs tw:text-foreground">
                {row.name}
              </dt>
              <dd className="tw:m-0 tw:text-xs tw:text-muted-foreground tw:sm:mb-0">
                {row.desc}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )

    case "map":
      return <MapControl field={field} />

    case "static":
      return (
        <p className="tw:text-sm tw:break-words tw:text-muted-foreground">
          {field.staticValue ?? field.value}
        </p>
      )
    default:
      return (
        /*
         * UNCONTROLLED, with `defaultValue`. Legacy reaches for
         * `UncontrolledTextInput` everywhere for the same reason: this record
         * round-trips through the container's state, and a controlled input
         * whose value arrives a render later puts the caret back at the end of
         * the text on every keystroke. `formKey` on `SettingsFields` handles
         * the one case `defaultValue` cannot — a different record arriving
         * under the same field ids.
         */
        <Input
          id={field.id}
          defaultValue={field.value}
          placeholder={field.placeholder}
          disabled={field.disabled || field.locked}
          aria-invalid={field.error ? true : undefined}
          aria-describedby={field.error ? `${field.id}-error` : undefined}
          onChange={(event) =>
            change({ type: "set", field, value: event.target.value })
          }
          className={TAP_TARGET}
        />
      )
  }
}

/* ────────────────────────────────────────────────────────────────── field ── */

/**
 * One row of the settings list.
 *
 * Built on the `item` primitive - installed, unadopted until now, and exactly
 * this shape: `ItemContent` for the text, `ItemActions` for the trailing
 * control. The row is what stops a switch reading as a stray object floating in
 * the whitespace to the right of its label: bounded, separated from its
 * neighbours, with the switch as the row's trailing action rather than a thing
 * parked at the far edge of the page.
 *
 * `ItemTitle` / `ItemDescription` are NOT used: `FieldLabel` already carries
 * the help tooltip and the upgrade badge, and
 * `ItemDescription` line-clamps at two lines, which would silently truncate the
 * longer hints.
 */
export function SettingsFieldRow({
  field,
  divider = true,
  insetDivider = false,
  nested = false,
}: {
  field: SettingsField
  /** Hairline under the row. Off for the last row in a list. */
  divider?: boolean
  /**
   * Draw that hairline from the group's indent rather than the card edge, for
   * a row whose NEXT sibling is in the same governed group. A full-width line
   * between two governed rows would cut the group into separate settings,
   * which is the opposite of what the indent is saying; a short one separates
   * them while keeping the block whole.
   *
   * A pseudo-element rather than `border-b`, because a border spans the item's
   * whole width and there is nothing to offset it by.
   */
  insetDivider?: boolean
  /** Governed by the field above it — see `dependsOn`. */
  nested?: boolean
}) {
  const change = useSettingsChange()
  const row = cn(
    /* The primitive rounds and borders each item; in a divided list we want one
       hairline between rows instead, so the borders are reset and only the
       bottom edge is drawn. */
    "tw:rounded-none tw:border-0 tw:py-3.5",
    /* A governed row is INDENTED so it reads as belonging to the control above
       rather than sitting beside it. No tint: the card stays white throughout,
       and the indent plus the suppressed hairline carry the relationship on
       their own. */
    nested ? "tw:pr-4 tw:pl-8" : "tw:px-4",
    divider &&
      (insetDivider
        ? /* `left-8` matches the row's own `pl-8`, so the line starts under the
             label rather than under the card's edge. Sides are set separately —
             `inset-x-0` and `left-8` are the same box in tailwind-merge's eyes
             and one would drop the other. */
          "tw:relative tw:after:absolute tw:after:right-0 tw:after:bottom-0 tw:after:left-8 tw:after:h-px tw:after:bg-border"
        : "tw:border-b tw:border-border")
  )

  if (isRow(field)) {
    return (
      <Item className={row}>
        <ItemContent className="tw:gap-1">
          {/*
            * ONE LABEL ACROSS THE TEXT BLOCK, so the row is the hit target
            * rather than the 32px switch. `flex-1` makes it span the empty
            * middle of the row too, which is most of the clickable area on a
            * wide screen.
            *
            * The help line sits inside it too: more area, and clicking a
            * description to toggle its own setting is what people expect.
            *
            * THE ⓘ TRIGGER IS SAFE INSIDE THE LABEL. A label does not forward
            * clicks that land on an interactive descendant, so opening the
            * tooltip does not also flip the switch. Verified in the browser
            * rather than taken from the spec: a synthetic click on the label's
            * empty middle fires one click on the Switch, and a click on the ⓘ
            * button fires none.
            *
            * The echo body is kept OUTSIDE the label anyway, since it holds its
            * own selects and inputs and has no business inside a label for a
            * different control.
            *
            * `asText` stops FieldLabel rendering a second, nested `<label>`.
            */}
          <label
            htmlFor={field.id}
            className={cn(
              "tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-1",
              field.disabled || field.locked
                ? "tw:cursor-default"
                : "tw:cursor-pointer tw:select-none"
            )}
          >
            <FieldLabel field={field} asText />
            <FieldFooter field={field} />
          </label>
          {field.kind === "echo" && field.checked ? (
            <div className="tw:pt-2">
              <EchoRows field={field} />
            </div>
          ) : null}
        </ItemContent>
        <ItemActions
          /* `ItemActions` is `items-center`, which is right for a one-line
             row but centres the switch against the whole tall body once echo
             is on - it drifted down beside the Days/Hours selects, away from
             the label it actually controls. Pin it to the top in that case
             only. */
          className={
            field.kind === "echo" && field.checked
              ? "tw:self-start tw:pt-0.5"
              : undefined
          }
        >
          {/* `locked` is a plan gate, and legacy still lets the switch be
              pressed there — it opens the upgrade modal instead of storing
              anything. So the change is reported either way and the host
              decides; only `disabled` refuses outright. */}
          <Switch
            id={field.id}
            checked={field.checked}
            disabled={field.disabled || field.locked}
            aria-label={field.label}
            onCheckedChange={(checked) =>
              change({ type: "toggle", field, checked })
            }
          />
        </ItemActions>
      </Item>
    )
  }

  return (
    <Item className={row}>
      <ItemContent className="tw:gap-2">
        <FieldLabel field={field} />
        {/* The control, and under it the paired checkbox where it has one. */}
        <div className="tw:flex tw:w-full tw:flex-col tw:gap-2">
          <div
            className={cn(
              /* Wide widgets (rule stacks, tag pools, the map) fill the row;
                 everything else is capped so a two-line select does not become
                 a 900px box. */
              "tw:w-full tw:min-w-0",
              field.span === "full"
                ? "tw:max-w-full"
                : CONTROL_WIDTH[field.width ?? "full"]
            )}
          >
            <Control field={field} />
          </div>
          {field.pairedCheck ? (
            <PairedCheck field={field} pair={field.pairedCheck} />
          ) : null}
        </div>
        <FieldFooter field={field} />
      </ItemContent>
    </Item>
  )
}

/** The checkbox that sits under a control. See `pairedCheck`. */
function PairedCheck({
  field,
  pair,
}: {
  field: SettingsField
  pair: NonNullable<SettingsField["pairedCheck"]>
}) {
  const change = useSettingsChange()
  const disabled = pair.disabled || field.disabled || field.locked

  return (
    /* The label WRAPS the checkbox rather than pointing at it with `htmlFor`,
       so the whole "box + text" is one target. At this size the box alone is
       a 16px hit area, which is under every tap-target guideline going. */
    <label
      className={cn(
        "tw:flex tw:w-fit tw:items-center tw:gap-2",
        META_TEXT,
        disabled ? "tw:cursor-default" : "tw:cursor-pointer tw:select-none"
      )}
    >
      <Checkbox
        id={pair.id}
        checked={pair.checked}
        disabled={disabled}
        onCheckedChange={(checked) =>
          change({
            type: "toggle",
            /* The pair is its own param, so the host sees a change against the
               pair's name rather than the input's. */
            field: { ...field, id: pair.id, name: pair.name, label: pair.label },
            /* Radix hands back `"indeterminate"` for a tri-state box. This one
               is binary, but the type includes it, so it is narrowed here
               rather than cast away. */
            checked: checked === true,
          })
        }
      />
      <span>{pair.label}</span>
    </label>
  )
}

/* ──────────────────────────────────────────────────────────────── section ── */

/**
 * One section's fields, stacked in a single column at a reading measure.
 * `span: "full"` lets a field break that measure - rule stacks, tag pools, the
 * location map.
 */
export function SettingsFields({
  fields,
  formKey,
  className,
}: {
  fields: SettingsField[]
  /**
   * Remounts the list when it changes. Needed because the text inputs are
   * uncontrolled: `defaultValue` is read once, so a DIFFERENT RECORD arriving
   * under the same field ids — moving from one automation's settings to
   * another without leaving the screen — would leave the previous record's
   * text sitting in the boxes. Pass the record's id.
   */
  formKey?: string
  className?: string
}) {
  return (
    /*
     * A DIVIDED LIST, not a loose stack.
     *
     * Two problems it solves at once. Scanning: identical label / control / help
     * stacks separated only by whitespace read as one undifferentiated wall, and
     * a hairline per row gives the eye somewhere to land. Floating controls: a
     * switch with nothing around it looks unrelated to the label at the other
     * end of the row, and a bounded row makes it the row's trailing action.
     *
     * `ItemGroup` carries `role="list"`, so the structure is real rather than
     * decorative. Its default `gap-4` is dropped because the hairlines do the
     * separating here.
     */
    <ItemGroup
      key={formKey}
      className={cn(
        "tw:gap-0 tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:bg-card",
        className
      )}
    >
      {fields.map((field, i) => (
        <SettingsFieldRow
          key={field.id}
          field={field}
          /*
           * THREE CASES, and the group's two edges are the reason:
           *
           * - The row that OPENS a group (the control itself) drops its
           *   hairline, so the block joins the picker it answers to.
           * - Between two rows of the SAME group the hairline is inset to the
           *   indent — enough to separate two settings, not enough to break
           *   the block apart.
           * - The row that CLOSES a group keeps the full-width line, because
           *   what follows is back at the top level and the edge should line
           *   up with every other row's.
           */
          divider={
            i < fields.length - 1 &&
            !(fields[i + 1]?.dependsOn && !field.dependsOn)
          }
          insetDivider={Boolean(
            field.dependsOn && fields[i + 1]?.dependsOn === field.dependsOn
          )}
          nested={Boolean(field.dependsOn)}
        />
      ))}
    </ItemGroup>
  )
}

/**
 * Field-grid skeleton, for the wait before the record arrives. Uses the
 * `skeleton` primitive rather than a hand-rolled `animate-pulse` div - it is
 * installed, CLAUDE.md says to prefer it in new work, and the older kit pages
 * that hand-roll theirs predate it.
 *
 * Deliberately generic: the real tab set and field list are not knowable until
 * the record loads (legacy picks them off the input type), so this shows a
 * plausible grid rather than pretending to know the shape.
 */
export function SettingsFieldsSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      aria-hidden
      className="tw:w-full tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:bg-card"
    >
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className={cn(
            "tw:flex tw:w-full tw:flex-col tw:gap-2 tw:px-4 tw:py-3.5",
            i < rows - 1 && "tw:border-b tw:border-border"
          )}
        >
          <Skeleton className="tw:h-4 tw:w-32" />
          <Skeleton className="tw:h-11 tw:w-full tw:max-w-[480px] tw:md:h-8" />
          <Skeleton className="tw:h-3 tw:w-48" />
        </div>
      ))}
    </div>
  )
}

/**
 * Radix Tooltip.Root throws without a Provider ancestor, so the form supplies
 * one rather than depending on prod having wrapped the app - that keeps the
 * file drop-in. It renders no DOM, and nesting inside a root provider prod may
 * already have is harmless. Bare, exactly as connect-social.tsx has it: the
 * primitive already defaults `delayDuration` to 0.
 */
export function SettingsFormProvider({
  children,
  onChange,
}: {
  children: React.ReactNode
  /**
   * Every control's one call. Omit it and the form renders exactly as it did
   * before there was a host, which is what the preview kit needs.
   */
  onChange?: (change: SettingsChange) => void
}) {
  return (
    <TooltipProvider>
      <SettingsChangeContext.Provider value={onChange}>
        {children}
      </SettingsChangeContext.Provider>
    </TooltipProvider>
  )
}
